import { FC, useEffect } from "react"
import { useTabs, parseTabId } from "@/contexts/tabsContext"
import { ScrollTextIcon, EyeIcon, BrainIcon, SettingsIcon, MessageSquareIcon, FileTextIcon, ActivityIcon, BarChart2Icon } from "lucide-react"
import { UserCircleIcon } from "@heroicons/react/24/outline"
import Dynamic from '@/lib/dynamic'
import ComponentSpinner from "@/components/ui/componentSpinner"
import { TabData } from "rc-dock"
import { TabTitle } from "@/chat2/components/chatlayout/TabTitle"

// ============================================================================
// DYNAMIC IMPORTS
// ============================================================================

const LogsAdminPage = Dynamic(() => import("@/chat2/components/admin/LogsAdminPage2").then(mod => ({ default: mod.LogsAdminPage2 })), { ssr: false, loading: () => <ComponentSpinner /> })
const PerfAnalyzerPage = Dynamic(() => import("@/chat2/components/admin/PerfAnalyzerPage").then(mod => ({ default: mod.PerfAnalyzerPage })), { ssr: false, loading: () => <ComponentSpinner /> })
const UserListPanel = Dynamic(() => import("@/chat2/components/admin/users/UserListPanel").then(mod => ({ default: mod.UserListPanel })), { ssr: false, loading: () => <ComponentSpinner /> })
const UserConsole = Dynamic(() => import("@/chat2/components/admin/users/console/UserConsole").then(mod => ({ default: mod.UserConsole })), { ssr: false, loading: () => <ComponentSpinner /> })
const RolesAdminPage = Dynamic(() => import("@/chat2/components/admin/rolesAdminPage").then(mod => ({ default: mod.RolesAdminPage })), { ssr: false, loading: () => <ComponentSpinner /> })
const ProfilesAdminV2 = Dynamic(() => import("@/components/llmProfiles/ProfilesAdminV2").then(mod => ({ default: mod.ProfilesAdminV2 })), { ssr: false, loading: () => <ComponentSpinner /> })
const PlansAdminPage2 = Dynamic(() => import("@/chat2/components/admin/plansAdminPage2").then(mod => ({ default: mod.PlansAdminPage2 })), { ssr: false, loading: () => <ComponentSpinner /> })
const SettingsAdminPage = Dynamic(() => import("@/chat2/components/admin/settingsAdminPage").then(mod => ({ default: mod.SettingsAdminPage })), { ssr: false, loading: () => <ComponentSpinner /> })
const WorkspaceDefaultsAdminPage = Dynamic(() => import("@/chat2/components/admin/workspaceDefaultsAdminPage").then(mod => ({ default: mod.WorkspaceDefaultsAdminPage })), { ssr: false, loading: () => <ComponentSpinner /> })
const DefaultPromptsAdminPage = Dynamic(() => import("@/chat2/components/admin/defaultPromptsAdminPage").then(mod => ({ default: mod.DefaultPromptsAdminPage })), { ssr: false, loading: () => <ComponentSpinner /> })
const WelcomeSettingsAdminPage = Dynamic(() => import("@/chat2/components/admin/welcomeSettingsAdminPage").then(mod => ({ default: mod.WelcomeSettingsAdminPage })), { ssr: false, loading: () => <ComponentSpinner /> })
const FetchSettingsAdminPage = Dynamic(() => import("@/chat2/components/admin/fetchSettingsAdminPage").then(mod => ({ default: mod.FetchSettingsAdminPage })), { ssr: false, loading: () => <ComponentSpinner /> })
const BudgetDefaultsAdminPage = Dynamic(() => import("@/chat2/components/admin/budgetDefaultsAdminPage").then(mod => ({ default: mod.BudgetDefaultsAdminPage })), { ssr: false, loading: () => <ComponentSpinner /> })
const PonderDefaultsAdminPage = Dynamic(() => import("@/chat2/components/admin/ponderDefaultsAdminPage").then(mod => ({ default: mod.PonderDefaultsAdminPage })), { ssr: false, loading: () => <ComponentSpinner /> })
const ProviderProfilesAdmin = Dynamic(() => import("@/components/admin/providerProfiles/ProviderProfilesAdmin").then(mod => ({ default: mod.ProviderProfilesAdmin })), { ssr: false, loading: () => <ComponentSpinner /> })
const MetricsAdminPage = Dynamic(() => import("@/components/admin/metrics/MetricsAdminPage").then(mod => ({ default: mod.MetricsAdminPage })), { ssr: false, loading: () => <ComponentSpinner /> })

// ============================================================================
// WRAPPER COMPONENTS
// ============================================================================

/** Common wrapper for admin tab content - enables scrolling within dock panel */
const AdminTabWrapper: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="h-full flex flex-col overflow-hidden">{children}</div>
)

export const AdminLogsTab: FC<{ tabId: string }> = ({ tabId }) => {
  const tabs = useTabs()
  const parsed = parseTabId(tabId)
  
  useEffect(() => {
    tabs?.setTabMetadata(tabId, { title: "Logs", icon: <ScrollTextIcon className="w-4 h-4" /> })
  }, [tabId, tabs])
  
  return <AdminTabWrapper><LogsAdminPage id={parsed.entityId || "0"} /></AdminTabWrapper>
}

export const AdminUsersTab: FC<{ tabId: string }> = ({ tabId }) => {
  const tabs = useTabs()

  useEffect(() => {
    tabs?.setTabMetadata(tabId, { title: "Users", icon: <UserCircleIcon className="w-4 h-4" /> })
  }, [tabId, tabs])

  return <AdminTabWrapper><UserListPanel /></AdminTabWrapper>
}

export const AdminUserConsoleTab: FC<{ tabId: string }> = ({ tabId }) => {
  const tabs = useTabs()
  const parsed = parseTabId(tabId)
  const userId = parsed.entityId || ""

  useEffect(() => {
    tabs?.setTabMetadata(tabId, {
      title: userId ? `User ${userId.slice(0, 8)}…` : "User Console",
      icon: <UserCircleIcon className="w-4 h-4" />,
    })
  }, [tabId, tabs, userId])

  if (!userId) {
    return (
      <AdminTabWrapper>
        <div className="p-6 text-sm text-muted-foreground">Missing user id.</div>
      </AdminTabWrapper>
    )
  }

  return (
    <AdminTabWrapper>
      <UserConsole userId={userId} />
    </AdminTabWrapper>
  )
}

export const AdminPerfAnalyzerTab: FC<{ tabId: string }> = ({ tabId }) => {
  const tabs = useTabs()

  useEffect(() => {
    tabs?.setTabMetadata(tabId, { title: "Perf Analyzer", icon: <ActivityIcon className="w-4 h-4" /> })
  }, [tabId, tabs])

  return <AdminTabWrapper><PerfAnalyzerPage /></AdminTabWrapper>
}

export const AdminRolesTab: FC<{ tabId: string }> = ({ tabId }) => {
  const tabs = useTabs()
  const parsed = parseTabId(tabId)
  
  useEffect(() => {
    tabs?.setTabMetadata(tabId, { title: "Roles", icon: <EyeIcon className="w-4 h-4" /> })
  }, [tabId, tabs])
  
  return <AdminTabWrapper><RolesAdminPage id={parsed.entityId || "0"} /></AdminTabWrapper>
}

export const AdminModelProfilesTab: FC<{ tabId: string }> = ({ tabId }) => {
  const tabs = useTabs()
  
  useEffect(() => {
    tabs?.setTabMetadata(tabId, { title: "Model Profiles", icon: <BrainIcon className="w-4 h-4" /> })
  }, [tabId, tabs])
  
  return <AdminTabWrapper><ProfilesAdminV2 /></AdminTabWrapper>
}

export const AdminProviderProfilesTab: FC<{ tabId: string }> = ({ tabId }) => {
  const tabs = useTabs()
  
  useEffect(() => {
    tabs?.setTabMetadata(tabId, { title: "Provider Profiles", icon: <SettingsIcon className="w-4 h-4" /> })
  }, [tabId, tabs])
  
  return <AdminTabWrapper><ProviderProfilesAdmin /></AdminTabWrapper>
}

export const AdminPlansTab: FC<{ tabId: string }> = ({ tabId }) => {
  const tabs = useTabs()
  
  useEffect(() => {
    tabs?.setTabMetadata(tabId, { title: "Plans", icon: <EyeIcon className="w-4 h-4" /> })
  }, [tabId, tabs])
  
  return <AdminTabWrapper><PlansAdminPage2 /></AdminTabWrapper>
}

export const AdminSettingsTab: FC<{ tabId: string }> = ({ tabId }) => {
  const tabs = useTabs()
  
  useEffect(() => {
    tabs?.setTabMetadata(tabId, { title: "Settings", icon: <SettingsIcon className="w-4 h-4" /> })
  }, [tabId, tabs])
  
  return <AdminTabWrapper><SettingsAdminPage /></AdminTabWrapper>
}

export const AdminWorkspaceDefaultsTab: FC<{ tabId: string }> = ({ tabId }) => {
  // Note: setTabMetadata with onClose callback is done inside WorkspaceDefaultsAdminPage
  // to tie form persistence cleanup to tab close
  return <AdminTabWrapper><WorkspaceDefaultsAdminPage tabId={tabId} /></AdminTabWrapper>
}

export const AdminDefaultPromptsTab: FC<{ tabId: string }> = ({ tabId }) => {
  // Note: setTabMetadata with onClose callback is done inside DefaultPromptsAdminPage
  // to tie form persistence cleanup to tab close
  return <AdminTabWrapper><DefaultPromptsAdminPage tabId={tabId} /></AdminTabWrapper>
}

export const AdminWelcomeSettingsTab: FC<{ tabId: string }> = ({ tabId }) => {
  // Note: setTabMetadata with onClose callback is done inside WelcomeSettingsAdminPage
  return <AdminTabWrapper><WelcomeSettingsAdminPage tabId={tabId} /></AdminTabWrapper>
}

export const AdminFetchSettingsTab: FC<{ tabId: string }> = ({ tabId }) => {
  // Note: setTabMetadata with onClose callback is done inside FetchSettingsAdminPage
  return <AdminTabWrapper><FetchSettingsAdminPage tabId={tabId} /></AdminTabWrapper>
}

export const AdminBudgetDefaultsTab: FC<{ tabId: string }> = ({ tabId }) => {
  // Note: setTabMetadata with onClose callback is done inside BudgetDefaultsAdminPage
  return <AdminTabWrapper><BudgetDefaultsAdminPage tabId={tabId} /></AdminTabWrapper>
}

export const AdminPonderDefaultsTab: FC<{ tabId: string }> = ({ tabId }) => {
  // Note: setTabMetadata with onClose callback is done inside PonderDefaultsAdminPage
  return <AdminTabWrapper><PonderDefaultsAdminPage tabId={tabId} /></AdminTabWrapper>
}

export const AdminMetricsTab: FC<{ tabId: string }> = ({ tabId }) => {
  const tabs = useTabs()

  useEffect(() => {
    tabs?.setTabMetadata(tabId, { title: "Metrics", icon: <BarChart2Icon className="w-4 h-4" /> })
  }, [tabId, tabs])

  return <AdminTabWrapper><MetricsAdminPage /></AdminTabWrapper>
}

// ============================================================================
// RENDERERS
// ============================================================================

export const AdminLogsTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminLogsTab tabId={id} />
})

export const AdminUsersTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminUsersTab tabId={id} />
})

export const AdminUserConsoleTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminUserConsoleTab tabId={id} />
})

export const AdminPerfAnalyzerTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminPerfAnalyzerTab tabId={id} />
})

export const AdminRolesTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminRolesTab tabId={id} />
})

export const AdminModelProfilesTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminModelProfilesTab tabId={id} />
})

export const AdminProviderProfilesTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminProviderProfilesTab tabId={id} />
})

export const AdminPlansTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminPlansTab tabId={id} />
})

export const AdminSettingsTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminSettingsTab tabId={id} />
})

export const AdminWorkspaceDefaultsTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminWorkspaceDefaultsTab tabId={id} />
})

export const AdminDefaultPromptsTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminDefaultPromptsTab tabId={id} />
})

export const AdminWelcomeSettingsTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminWelcomeSettingsTab tabId={id} />
})

export const AdminFetchSettingsTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminFetchSettingsTab tabId={id} />
})
export const AdminBudgetDefaultsTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminBudgetDefaultsTab tabId={id} />
})

export const AdminPonderDefaultsTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminPonderDefaultsTab tabId={id} />
})

export const AdminMetricsTabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <AdminMetricsTab tabId={id} />
})